import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import api from "../config/api";
import AuthContext from "../context/AuthContext";
import Footer from "../components/Footer";

import {
  Briefcase,
  ArrowRight,
  Server,
  ShieldCheck,
  Megaphone,
  CheckCircle2,
  CheckCircle,
  FileText,
  Star,
  Sparkles,
  Terminal
} from "lucide-react";

// --- Data ---
const staticTestimonials = [
  {
    quote: "This was an amazing experience! I learned so much and built a real project that I could add to my portfolio. The mentorship was top-notch.",
    name: "Ranjeet Kumar",
    role: "Python Developer",
  },
  {
    quote: "Working on modern stacks allowed me to easily bridge the gap between academia and the real tech industry. Highly recommended for students.",
    name: "Azaz Alam",
    role: "Graphics Design Intern",
  },
  {
    quote: "I highly recommend Student-Era to any student looking to get a head start. The structured layout and expert support is unparalleled.",
    name: "Gulshan Kumar",
    role: "Web Developer",
  },
];

// --- Helpers ---
function parseMessageWithLinks(message) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = message.split(urlRegex);
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline text-indigo-300 hover:text-white transition-colors duration-150">
          {part}
        </a>
      );
    }
    return part;
  });
}

const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries || (error.code !== "ECONNABORTED" && !error.message?.includes("timeout"))) {
        throw error;
      }
      const delay = baseDelay * Math.pow(2, attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

// --- Components ---
const AnnouncementBar = () => {
  const [announcements, setAnnouncements] = useState([]);
  const show = true;

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const result = await retryWithBackoff(async () => {
          const { data } = await api.get("/announcements");
          return data;
        });
        if (result.data && result.data.length > 0) {
          setAnnouncements(result.data);
        }
      } catch (error) {
        console.error("Could not fetch announcements", error);
      }
    };
    fetchAnnouncements();
  }, []);

  if (!show || announcements.length === 0) return null;

  const marqueeText = announcements.map((a, i) => (
    <span key={i} className={i === 0 ? "mr-12" : "ml-12"}>
      <span className="bg-white text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider mr-3 align-middle shadow-sm">
        New Update
      </span>
      {parseMessageWithLinks(a.message)}
    </span>
  ));

  return (
    <div className="bg-indigo-600 text-white shadow-sm relative z-50">
      <div className="max-w-7xl mx-auto py-2.5 px-4 sm:px-6 lg:px-8 relative overflow-hidden flex items-center">
        <span className="flex-shrink-0 mr-3 animate-pulse opacity-80">
          <Megaphone className="h-4 w-4" />
        </span>
        <div className="w-full overflow-hidden text-sm font-medium tracking-wide">
          <span className="inline-block animate-[marquee-smooth_25s_linear_infinite] hover:[animation-play-state:paused] cursor-pointer whitespace-nowrap">
            {marqueeText}
          </span>
        </div>
      </div>
      <style>{`
        @keyframes marquee-smooth {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
};

const HeroSection = () => {
  const { isAuthenticated } = useContext(AuthContext);

  return (
    <div className="relative pt-16 pb-24 lg:pt-24 lg:pb-32 overflow-hidden">
      {/* Visual background gradient mesh */}
      <div className="absolute top-0 inset-x-0 h-full w-full bg-gradient-to-br from-indigo-50/50 via-slate-50 to-blue-50/30 -z-10"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Copywriting & Actions */}
          <div className="lg:col-span-7 text-left">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 font-bold text-xs border border-blue-200 mb-6 uppercase tracking-wider shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-blue-600" /> Virtual Internships Active
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight mb-6 leading-[1.15]">
              Launch Your Tech Career with <span className="text-gradient-premium">Student-Era</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mb-8 leading-relaxed">
              Gain real-world experience through premium virtual internships. Work on live projects, collaborate with experts, and build a verified portfolio to stand out to global employers.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                to="/internships"
                className="inline-flex items-center justify-center px-6 py-3.5 border border-transparent text-sm sm:text-base font-bold rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 hover-glow shadow-md shadow-indigo-100 transition-all duration-200 active:scale-[0.98]"
              >
                Explore Programs <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              {!isAuthenticated && (
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-6 py-3.5 border border-slate-200 text-sm sm:text-base font-bold rounded-xl text-slate-700 bg-white hover:bg-slate-50 transition-all duration-200 shadow-sm hover:border-slate-300"
                >
                  Create Free Account
                </Link>
              )}
              <Link
                to="/verify-certificate"
                className="inline-flex items-center justify-center px-6 py-3.5 border border-slate-200 text-sm sm:text-base font-bold rounded-xl text-slate-600 bg-white/50 hover:bg-white transition-all duration-200 shadow-sm"
              >
                Verify Credential
              </Link>
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-3 text-xs sm:text-sm font-semibold text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-500" />
                <span>Industry Standard Tech</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-500" />
                <span>Verified Certification</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-indigo-500" />
                <span>Expert Curated Projects</span>
              </div>
            </div>
          </div>

          {/* Right Column: Premium Visual Mockup */}
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-tr from-indigo-500 to-blue-500 opacity-20 blur-xl"></div>
            <div className="relative glass-card rounded-2xl border border-white/60 shadow-2xl p-6 overflow-hidden">
              {/* Window Header */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-200/60 mb-4">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-400"></span>
                  <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                  <span className="w-3 h-3 rounded-full bg-green-400"></span>
                </div>
                <div className="flex items-center gap-1 bg-slate-100 px-3 py-1 rounded-md text-[10px] font-mono text-slate-500">
                  <Terminal className="w-3 h-3" /> student-era.sh
                </div>
              </div>
              {/* Terminal/Interface Mockup */}
              <div className="space-y-4 font-sans">
                <div className="bg-slate-900 text-slate-100 rounded-xl p-4 font-mono text-xs space-y-2 leading-relaxed shadow-inner">
                  <p className="text-indigo-400">$ npm run start:career</p>
                  <p className="text-emerald-400">&gt; Initializing virtual workspace...</p>
                  <p className="text-slate-400">&gt; Setting up react-app, tailwindcss, node.js, express</p>
                  <p className="text-slate-400">&gt; Project structure initialized successfully!</p>
                  <p className="text-yellow-400">&gt; Status: 4 Tasks Active | Progress: 75%</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-sm">
                      JS
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">MERN Stack Internship</p>
                      <p className="text-[10px] text-slate-500">Full-Stack Track</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded uppercase">
                    Active
                  </span>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm space-y-2">
                  <div className="flex justify-between items-center text-[10px] font-bold text-slate-600">
                    <span>Internship Milestones</span>
                    <span className="text-indigo-600">3 of 4 Done</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-indigo-600 h-1.5 rounded-full" style={{ width: "75%" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CertifiedSection = () => (
  <div className="bg-white py-12 border-y border-slate-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center">
      <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-3" />
      <h2 className="text-xl font-bold text-slate-900 mb-2">
        Officially Recognized Organization
      </h2>
      <p className="text-slate-500 text-sm max-w-xl mb-8">
        All certificates issued by Student Era meet high compliance standards, giving you a verifiable credential to display on LinkedIn and resumes.
      </p>
      
      {/* Logos Container */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16 opacity-75">
        <div className="flex flex-col items-center">
          <img src="/msme.png" alt="MSME Registered" className="h-12 w-auto object-contain hover:scale-105 transition-all duration-200" />
          <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-1.5">MSME Certified</span>
        </div>
        <div className="flex flex-col items-center">
          <img src="/startup india.png" alt="Startup India Registered" className="h-12 w-auto object-contain hover:scale-105 transition-all duration-200" />
          <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-1.5">Startup India</span>
        </div>
      </div>
    </div>
  </div>
);

const Feature = ({ icon, title, description }) => (
  <div className="bg-white flex flex-col items-start p-8 rounded-2xl border border-slate-200 hover-glow shadow-sm">
    <div className="bg-indigo-50 text-indigo-600 rounded-xl p-3 mb-5 border border-indigo-100">
      {icon}
    </div>
    <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
    <p className="text-slate-500 text-sm leading-relaxed">{description}</p>
  </div>
);

const FeaturesSection = () => (
  <div className="py-20 bg-slate-50/50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16 max-w-3xl mx-auto">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs uppercase tracking-wider mb-3">
          Our Framework
        </span>
        <h2 className="text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">
          A Better Way to Build Your Portfolio
        </h2>
        <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
          Our platform bridges the gap between academic theory and real-world execution. Here's what you get when you join our internship programs.
        </p>
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Feature
          icon={<Briefcase className="h-6 w-6" />}
          title="Real-World Execution"
          description="Complete standardized deliverables simulating exact workflows expected by top tech employers today."
        />
        <Feature
          icon={<Server className="h-6 w-6" />}
          title="Live Project Deployment"
          description="We guide you on deploying your frontend and backend so your work exists live on the internet."
        />
        <Feature
          icon={<ShieldCheck className="h-6 w-6" />}
          title="Verifiable Credentials"
          description="Each completed internship grants a unique cryptographic certificate easily verifiable by HR teams."
        />
      </div>
    </div>
  </div>
);

const InternshipCard = ({ internship }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col hover-glow hover:border-indigo-300 shadow-sm transition-all duration-300">
    <div className="flex items-start justify-between mb-4">
      <h3 className="text-lg font-bold text-slate-900 line-clamp-2 leading-snug">
        {internship.title}
      </h3>
      {internship.tag && (
        <span className="px-2.5 py-1 rounded bg-indigo-50 text-indigo-700 text-[9px] font-extrabold uppercase tracking-wider border border-indigo-100 shrink-0 ml-3">
          {internship.tag}
        </span>
      )}
    </div>
    <p className="text-sm text-slate-500 mb-6 flex-1 line-clamp-3 leading-relaxed">
      {internship.shortDescription || internship.description}
    </p>
    {Array.isArray(internship.technologies) && (
      <div className="flex flex-wrap gap-1.5 mb-6">
        {internship.technologies.slice(0, 4).map((tech) => (
          <span
            key={tech}
            className="bg-slate-50 text-slate-600 text-xs font-semibold px-2.5 py-1 rounded-lg border border-slate-100"
          >
            {tech}
          </span>
        ))}
      </div>
    )}
    <div className="pt-4 border-t border-slate-100 mt-auto flex items-center justify-between">
      <span className="text-xs font-bold text-slate-400">Duration: {internship.duration || "N/A"}</span>
      <Link
        to={`/internships/${internship._id}`}
        className="flex items-center text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
      >
        View Details <ArrowRight className="ml-1.5 h-4 w-4" />
      </Link>
    </div>
  </div>
);

const LatestInternships = () => {
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchInternships = async () => {
      try {
        const result = await retryWithBackoff(async () => {
          const res = await api.get("/internships/public");
          return res.data;
        });
        setInternships(result.data);
      } catch (err) {
        console.error("Error fetching internships:", err);
        setError("Failed to load internships.");
      } finally {
        setLoading(false);
      }
    };
    fetchInternships();
  }, []);

  return (
    <div className="py-20 bg-white border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs uppercase tracking-wider mb-3">
            Available Batches
          </span>
          <h2 className="text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">
            Open Internship Programs
          </h2>
          <p className="max-w-2xl mx-auto text-base sm:text-lg text-slate-500">
            Select a learning track tailored to modern tech stacks and start building today.
          </p>
        </div>

        {loading && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-slate-50 border border-slate-100 rounded-2xl p-6 space-y-4 animate-pulse">
                <div className="h-6 bg-slate-200 rounded-md w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded-md w-full"></div>
                <div className="h-4 bg-slate-200 rounded-md w-5/6"></div>
                <div className="h-8 bg-slate-200 rounded-md w-1/3"></div>
              </div>
            ))}
          </div>
        )}
        {error && <div className="text-center text-red-600 p-4 bg-red-50 border border-red-200 rounded-xl">{error}</div>}

        {!loading && !error && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {internships.length > 0 ? (
              internships.slice(0, 6).map((internship) => (
                <InternshipCard key={internship._id} internship={internship} />
              ))
            ) : (
              <div className="col-span-full text-center p-12 bg-slate-50 border border-slate-200 rounded-2xl">
                <Briefcase className="h-8 w-8 text-slate-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-slate-900">No Openings Currently</h3>
                <p className="text-slate-500 mt-1">Please check back later for new batches.</p>
              </div>
            )}
          </div>
        )}

        {!loading && !error && internships.length > 0 && (
          <div className="mt-12 text-center">
            <Link to="/internships" className="inline-flex items-center justify-center px-6 py-3 border border-slate-200 text-sm font-bold rounded-xl text-slate-700 bg-white hover:bg-slate-50 hover-glow transition-all duration-200 shadow-sm">
              View All Programs
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const result = await retryWithBackoff(async () => {
          const { data } = await api.get("/testimonials");
          return data;
        });
        setTestimonials(result.data);
      } catch (error) {
        console.error("Could not fetch testimonials", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const displayTestimonials = loading || testimonials.length === 0 ? staticTestimonials : testimonials;

  return (
    <div className="py-20 bg-slate-50/50 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs uppercase tracking-wider mb-3">
            Success Stories
          </span>
          <h2 className="text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">
            Hear from Our Students
          </h2>
          <p className="max-w-2xl mx-auto text-base sm:text-lg text-slate-500">
            Real feedback from graduates who used our platform to build engineering careers.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {displayTestimonials.slice(0, 3).map((testimonial, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 flex flex-col justify-between hover:shadow-md transition-shadow duration-200">
              <div>
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-6 italic">
                  "{testimonial.message || testimonial.quote}"
                </p>
              </div>
              <div className="flex items-center">
                <img
                  className="w-10 h-10 rounded-full border border-slate-200 bg-slate-100 object-cover"
                  src={testimonial.image || testimonial.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.name)}&background=e0e7ff&color=4f46e5`}
                  alt={testimonial.name}
                />
                <div className="ml-3">
                  <p className="font-bold text-slate-900 text-sm leading-none mb-1">{testimonial.name}</p>
                  <p className="text-slate-500 text-xs">{testimonial.designation || testimonial.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  return (
    <div className="bg-white font-sans text-slate-900 border-t-0">
      <AnnouncementBar />
      <HeroSection />
      <CertifiedSection />
      <FeaturesSection />
      <LatestInternships />
      <TestimonialsSection />
      <Footer />
    </div>
  );
};

export default Home;

